<script lang="ts">
  import { onMount } from "svelte";
  import type { CropRegion } from "./Image";
  import inView from "$lib/utils/inView";
  import { memoryManager } from "$lib/utils/memoryManager";

  export let smImage: string;
  export let mdImage: string;
  export let lgImage = mdImage;
  export let xlImage = lgImage || mdImage;
  export let altText: string;
  export let imageConfig = {
    smSize: { width: 767, height: 767 },
    mdSize: { width: 1199, height: 782 },
    lgSize: { width: 1200, height: 480 },
    xlSize: { width: 1600, height: 480 },
  };
  export let fitOrFill: string = "fit";
  export let cover = false;
  export let noTransform = false;
  export let shopifyImage = false;
  export let eager = false;
  export let enableMemoryManagement = true;

  let pictureEl: HTMLPictureElement;
  let wrapperEl: HTMLDivElement;
  let loadingAttr: "eager" | "lazy" = eager ? "eager" : "lazy";
  let imageLoaded = false;
  let imageKey = "";
  let imageUnloaded = false;
  let originalSources: { sm: string; md: string; lg: string; xl: string } = {
    sm: "",
    md: "",
    lg: "",
    xl: "",
  };

  $: if (smImage || mdImage) {
    lgImage = mdImage;
    xlImage = lgImage || mdImage;
  }

  $: if (smImage || mdImage) {
    imageLoaded = false;
    imageUnloaded = false;
    imageKey = `${smImage}-${mdImage}-${lgImage}-${xlImage}-${Date.now()}`;
    // Store original sources for memory management
    originalSources = {
      sm: smImage,
      md: mdImage,
      lg: lgImage,
      xl: xlImage,
    };
  }

  // Extract blurhash from URL
  function extractBlurhash(image: string): string | null {
    if (!image) return null;
    const match = image.match(/blurhash=([^&]*)/);
    if (match && match[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch (e) {
        return match[1];
      }
    }
    return null;
  }

  // Generate placeholder
  function generatePlaceholderImage(image: string) {
    if (!image) return "";

    if (shopifyImage) {
      return `${image}?width=40`;
    }

    if (image.includes(".flite.io")) {
      return image;
    }

    const [base, rest] = image?.split("/upload/") || ["", ""];
    const parts = rest?.split("/");
    const versionOrIdIndex = parts?.findIndex(part => part.startsWith("v"));

    // Extract existing transformations (like crops)
    const existingTransforms =
      parts?.slice(0, versionOrIdIndex).join("/") || "";
    const versionAndId = parts?.slice(versionOrIdIndex).join("/");

    // Combine existing transforms with placeholder quality/size
    const placeholderTransforms = `f_auto,q_10${
      noTransform ? "" : `,c_fit,w_${40}`
    }`;

    // If there are existing transforms, append new ones with a slash
    const allTransforms = existingTransforms
      ? `${existingTransforms}/${placeholderTransforms}`
      : placeholderTransforms;

    return `${base}/upload/${allTransforms}/${versionAndId}`;
  }

  // Try to get blurhash first, fall back to placeholder image
  $: blurhash = extractBlurhash(smImage) || extractBlurhash(mdImage);
  $: placeholderUrl = generatePlaceholderImage(smImage);

  // For blurhash, we'll need to import and use the blurhash library
  let blurhashDataUrl: string | null = null;

  // Reset blurhash when source changes
  $: if (smImage || mdImage) {
    blurhashDataUrl = null;
  }

  $: if (blurhash && typeof window !== "undefined") {
    import("blurhash")
      .then(({ decode }) => {
        try {
          // Decode blurhash to pixels
          const pixels = decode(blurhash, 32, 32);

          // Create canvas and draw pixels
          const canvas = document.createElement("canvas");
          canvas.width = 32;
          canvas.height = 32;
          const ctx = canvas.getContext("2d");

          if (ctx) {
            const imageData = ctx.createImageData(32, 32);
            imageData.data.set(pixels);
            ctx.putImageData(imageData, 0, 0);

            // Convert to data URL
            blurhashDataUrl = canvas.toDataURL();
          }
        } catch (error) {
          console.warn("Failed to decode blurhash:", error);
          // Fall back to regular placeholder
          blurhashDataUrl = null;
        }
      })
      .catch(error => {
        console.warn("Failed to load blurhash library:", error);
      });
  }

  // Ensure the URL is properly formatted for CSS
  function escapeUrlForCss(url: string): string {
    if (!url) return "";
    // If it's a data URL or already has quotes, return as is
    if (url.startsWith("data:") || url.includes('"') || url.includes("'")) {
      return url;
    }
    // Otherwise, ensure special characters are escaped
    return url.replace(/'/g, "\\'").replace(/"/g, '\\"');
  }

  $: backgroundUrl = escapeUrlForCss(blurhashDataUrl || placeholderUrl);

  // Auto-detect above-fold images
  onMount(() => {
    if (!eager && pictureEl) {
      const rect = pictureEl.getBoundingClientRect();
      const isAboveFold = rect.top < window.innerHeight;
      if (isAboveFold) {
        // Change loading to eager for above-fold images
        const img = pictureEl.querySelector("img");
        if (img) {
          img.loading = "eager";
        }
      }
    }

    // Handle image load event
    const img = pictureEl?.querySelector("img");
    if (img) {
      const handleLoad = () => {
        imageLoaded = true;
        if (wrapperEl) {
          wrapperEl.classList.add("image-loaded");
        }
      };

      // Check if already loaded
      if (img.complete && img.naturalHeight !== 0) {
        handleLoad();
      } else {
        img.addEventListener("load", handleLoad);
        img.addEventListener("error", handleLoad); // Hide placeholder on error too
      }
    }
  });

  // Simple image transformation
  function transformImage(
    image: string,
    size: { width: number; height: number; crop?: CropRegion },
    doubleSize = false,
  ): string {
    const width = doubleSize ? size.width * 2 : size.width;
    const height = doubleSize ? size.height * 2 : size.height;

    if (shopifyImage) {
      const basePath = image.split("?")[0];
      const queryParams = new URLSearchParams(image.split("?")[1]);
      const fileExtension = basePath.split(".").pop() || "jpg";
      const baseWithoutExt = basePath.substring(0, basePath.lastIndexOf("."));
      return `${baseWithoutExt}_${width}x${height}${
        size.crop ? `_crop_${size.crop}` : ""
      }.${fileExtension}?${queryParams.toString()}`;
    }

    if (image.includes(".flite.io")) {
      return image;
    }

    const [base, rest] = image?.split("/upload/") || ["", ""];
    const parts = rest?.split("/");
    const versionOrIdIndex = parts?.findIndex(part => part.startsWith("v"));

    // Extract existing transformations
    const existingTransforms =
      parts?.slice(0, versionOrIdIndex).join("/") || "";
    const versionAndId = parts?.slice(versionOrIdIndex).join("/");

    // Combine existing transforms with new ones
    const newTransforms = `f_auto,q_80${
      noTransform ? "" : `,c_${fitOrFill},w_${width},h_${height}`
    }`;

    // If there are existing transforms, append new ones with a slash
    const allTransforms = existingTransforms
      ? `${existingTransforms}/${newTransforms}`
      : newTransforms;

    return `${base}/upload/${allTransforms}/${versionAndId}`;
  }

  $: xlSrcSet = `${transformImage(
    xlImage,
    imageConfig.xlSize,
  )} 1x, ${transformImage(xlImage, imageConfig.xlSize, true)} 2x`;
  $: lgSrcSet = `${transformImage(
    lgImage,
    imageConfig.lgSize,
  )} 1x, ${transformImage(lgImage, imageConfig.lgSize, true)} 2x`;
  $: mdSrcSet = `${transformImage(
    mdImage,
    imageConfig.mdSize,
  )} 1x, ${transformImage(mdImage, imageConfig.mdSize, true)} 2x`;
  $: smSrcSet = `${transformImage(
    smImage,
    imageConfig.smSize,
  )} 1x, ${transformImage(smImage, imageConfig.smSize, true)} 2x`;

  $: fallbackSrc = transformImage(smImage, imageConfig.smSize);

  let isInView = false;

  function unloadImage() {
    if (imageUnloaded) return;

    imageUnloaded = true;
    imageLoaded = false;

    if (pictureEl) {
      const sources = pictureEl.querySelectorAll("source");
      sources.forEach(source => {
        source.srcset = "";
        source.removeAttribute("srcset");
      });

      const img = pictureEl.querySelector("img");
      if (img) {
        img.src = "";
        img.srcset = "";
        img.removeAttribute("src");
        img.removeAttribute("srcset");
      }
    }

    if (wrapperEl) {
      wrapperEl.classList.remove("image-loaded");
    }
  }

  function reloadImage() {
    if (!imageUnloaded) return;

    imageUnloaded = false;

    if (pictureEl) {
      const sources = pictureEl.querySelectorAll("source");
      const srcsets = [smSrcSet, mdSrcSet, lgSrcSet, xlSrcSet];

      sources.forEach((source, index) => {
        if (srcsets[index]) {
          source.srcset = srcsets[index];
        }
      });

      const img = pictureEl.querySelector("img");
      if (img) {
        img.src = fallbackSrc;

        // Re-attach load event listener
        const handleLoad = () => {
          imageLoaded = true;
          if (wrapperEl) {
            wrapperEl.classList.add("image-loaded");
          }
        };

        if (img.complete && img.naturalHeight !== 0) {
          handleLoad();
        } else {
          img.addEventListener("load", handleLoad, { once: true });
          img.addEventListener("error", handleLoad, { once: true });
        }
      }
    }
  }
</script>

<style lang="scss">
  .picture-wrapper {
    position: relative;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    overflow: hidden;
    display: block;
    // Ensure minimum height so background is visible
    min-height: 25px;

    // Blur effect for placeholder
    &::before {
      content: "";
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
      background-image: inherit;
      background-size: cover;
      background-position: center;
      filter: blur(20px);
      z-index: 1;
      opacity: 1;
      transition: opacity 0.3s ease-out;
    }

    &.image-loaded::before {
      opacity: 0;
    }

    // Preserve container dimensions when image is unloaded
    &.image-unloaded {
      // Maintain aspect ratio even when images are cleared
      aspect-ratio: 4/5 !important;
      background-color: $on-background;

      // Show a subtle placeholder background
      &::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 24px;
        height: 24px;
        opacity: 0.3;
        background-image: url("data:image/svg+xml,%3Csvg width='39' height='39' viewBox='0 0 39 39' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M38.6931 18.9288C38.17 20.4045 37.5482 21.8435 36.8318 23.2357C36.1189 24.6071 35.703 24.7407 32.599 24.909C32.9834 25.0567 33.5674 25.2746 34.201 25.5109C35.2243 25.8926 36.3766 26.3224 37.0248 26.5823C36.2232 27.6584 35.3179 28.6533 34.3219 29.5526C33.7032 30.0497 32.9133 30.2826 32.1238 30.2011H29.614L32.3713 32.5872C32.374 32.5926 32.3754 32.5986 32.3754 32.6045C32.3754 32.6106 32.374 32.6165 32.3713 32.6219C31.1734 33.3595 30.1783 33.9337 29.0396 34.5427C27.7872 35.1863 27.2476 35.0873 24.9259 33.7704C24.918 33.7644 24.9084 33.7611 24.8986 33.7611C24.8887 33.7611 24.8792 33.7644 24.8714 33.7704C24.8653 33.7783 24.8621 33.7878 24.8621 33.7976C24.8621 33.8075 24.8653 33.8171 24.8714 33.8248C25.1032 34.333 25.5716 35.2342 26.0492 36.153C26.1021 36.2548 26.1552 36.3568 26.208 36.4585C26.7872 37.5724 27.3317 38.6219 27.406 38.8546C27.4094 38.8607 27.4113 38.8675 27.4113 38.8744C27.4113 38.8813 27.4094 38.8881 27.406 38.8942C27.3932 38.8988 27.3792 38.8988 27.3664 38.8942H22.3762C22.3681 38.8936 22.3602 38.8911 22.3534 38.8868C22.3465 38.8825 22.3408 38.8765 22.3367 38.8694L20.8516 36.4684H20.802C20.7924 36.4696 20.7835 36.4744 20.7772 36.4817C20.7707 36.489 20.7673 36.4984 20.7674 36.5081L20.5446 38.9339H18.0248L17.797 36.5081C17.7971 36.4984 17.7937 36.489 17.7872 36.4817C17.7809 36.4744 17.7721 36.4696 17.7624 36.4684C17.7546 36.4652 17.7461 36.4635 17.7377 36.4635C17.7292 36.4635 17.7207 36.4652 17.713 36.4684L16.2278 38.8694C16.225 38.8764 16.2202 38.8825 16.2141 38.8869C16.2079 38.8912 16.2006 38.8939 16.1931 38.8942H11.198C11.1926 38.8968 11.1867 38.8982 11.1807 38.8982C11.1747 38.8982 11.1688 38.8968 11.1634 38.8942C11.1599 38.8881 11.1581 38.8813 11.1581 38.8744C11.1581 38.8675 11.1599 38.8607 11.1634 38.8546C11.2324 38.6385 11.7112 37.718 12.243 36.6957C12.2838 36.6171 12.325 36.538 12.3663 36.4585C12.4244 36.3468 12.4827 36.2347 12.5408 36.1229C13.0131 35.2145 13.4744 34.3274 13.7079 33.8248C13.7127 33.8166 13.7152 33.8072 13.7152 33.7976C13.7152 33.7881 13.7127 33.7788 13.7079 33.7704C13.7002 33.7644 13.6906 33.7611 13.6807 33.7611C13.6709 33.7611 13.6613 33.7644 13.6535 33.7704C11.3317 35.0873 10.7971 35.1863 9.54457 34.5427C8.40102 33.9337 7.40596 33.3595 6.21288 32.6219V32.5872L8.97527 30.2555H6.46536C5.6691 30.3204 4.87711 30.0875 4.24258 29.602C3.25276 28.6888 2.3587 27.677 1.57426 26.5823C2.0627 26.3948 2.84232 26.1044 3.63979 25.8074C4.54776 25.4693 5.47887 25.1225 6.02972 24.909C2.94555 24.7754 2.52475 24.6417 1.85149 23.2357L1.87624 23.211C1.1509 21.821 0.523946 20.3819 0 18.904C0.393411 18.9282 0.822043 18.9569 1.26288 18.9864C2.62493 19.0775 4.10351 19.1764 5.01981 19.1764C4.62874 18.8954 4.25655 18.6507 3.91267 18.4246C2.74167 17.6547 1.89902 17.1006 1.75743 16.0674C1.4307 13.2654 1.34159 9.8001 1.34159 9.8001L7.51982 13.9833C7.51982 13.9833 6.65844 12.8348 6.00001 11.8446C4.72774 9.90406 4.33169 8.52287 7.51982 3.27038C7.57922 3.40405 10.6089 8.48822 10.6089 8.48822C9.74755 6.04762 9.53468 4.27039 9.86141 3.11692C10.1881 1.96345 12.9753 0.473352 14.3713 0.0921631C14.406 0.309985 15.7079 13.4585 15.7079 13.4585C13.9207 14.2785 12.4677 15.6849 11.5898 17.4443C10.712 19.2038 10.4621 21.2106 10.8818 23.1316C11.3014 25.0527 12.3653 26.7724 13.897 28.0055C15.4285 29.2387 17.3358 29.9111 19.3021 29.9111C21.2683 29.9111 23.1755 29.2387 24.7071 28.0055C26.2388 26.7724 27.3027 25.0527 27.7223 23.1316C28.142 21.2106 27.8921 19.2038 27.0142 17.4443C26.1364 15.6849 24.6833 14.2785 22.8961 13.4585C22.8961 13.4585 24.1981 0.324836 24.1981 0.0921631C25.6535 0.473352 28.3862 1.90405 28.7427 3.08721C29.0991 4.27039 28.8417 6.00803 28.01 8.44862C28.104 8.24565 31.0198 3.43376 31.208 3.29514C34.3219 8.52288 33.9654 9.92883 32.6931 11.8694C32.0447 12.8793 31.208 14.008 31.208 14.008L37.3565 9.82486C37.3565 9.82486 37.2624 13.2754 36.9159 16.0922C36.7973 17.1253 35.9571 17.6793 34.7861 18.4514C34.4421 18.6782 34.0695 18.9238 33.6783 19.206C34.5301 19.2167 35.9012 19.1205 37.2093 19.0287C37.7256 18.9924 38.2322 18.9569 38.6931 18.9288ZM15.9605 27.2357C15.9605 27.2357 16.6634 28.013 19.307 28.013C21.9506 28.013 22.6535 27.2357 22.6535 27.2357V23.206H25.9307C26.3318 21.3511 26.127 19.4163 25.3466 17.6862H13.2574C12.477 19.4163 12.2722 21.3511 12.6733 23.206H15.9605V27.2357Z' fill='white'/%3E%3C/svg%3E");
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        z-index: 3;
      }
    }
  }

  .cover {
    height: 100%;
    width: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  picture {
    position: relative;
    display: block;
    z-index: 2;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
    max-width: 100%;
    position: relative;
    z-index: 2;
  }
</style>

{#key imageKey || `${smImage}-${mdImage}`}
  <div
    bind:this={wrapperEl}
    class="picture-wrapper inner {isInView ? 'fade-in' : ''}"
    class:image-loaded={imageLoaded}
    class:image-unloaded={imageUnloaded}
    style="background-image: url('{backgroundUrl}')"
    use:inView={{ threshold: 0.1 }}
    use:memoryManager={{
      enableImageUnloading: enableMemoryManagement,
      rootMargin: "50px",
      unloadRootMargin:
        typeof window !== "undefined" && window.innerWidth < 768
          ? "800px"
          : "1200px",
      debugMode: false,
    }}
    on:intersect={({ detail }) => {
      if (detail.type === "load") {
        if (detail.isIntersecting) {
          isInView = true;
          if (enableMemoryManagement) {
            reloadImage();
          }
        }
      } else if (detail.type === "unload" && enableMemoryManagement) {
        if (!detail.isIntersecting) {
          unloadImage();
        }
      } else if (typeof detail === "boolean" || (detail && !detail.type)) {
        if (detail || detail.isIntersecting) {
          isInView = true;
        }
      }
    }}
  >
    <picture bind:this={pictureEl} class:cover={cover || shopifyImage}>
      <source media="(max-width: 767px)" srcset={smSrcSet} />
      <source
        media="(min-width: 768px) and (max-width: 1279px)"
        srcset={mdSrcSet}
        sizes="50vw"
      />
      <source
        media="(min-width: 1280px) and (max-width: 1399px)"
        srcset={lgSrcSet}
        sizes="33vw"
      />
      <source media="(min-width: 1400px)" srcset={xlSrcSet} sizes="25vw" />
      <img
        src={fallbackSrc}
        width={imageConfig.smSize.width}
        height={imageConfig.smSize.height}
        alt={altText}
        loading={loadingAttr}
      />
    </picture>
  </div>
{/key}
